import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';

// A range calendar: click a day to set the start, click a second to close the
// range. Values are `YYYY-MM-DD` strings in local time — never Date objects
// and never ISO/UTC, so a range picked in IST means the days the admin saw and
// not the ones UTC midnight would shift them to.
//
// Follows SearchableDropdown's portal pattern: the popover renders into
// document.body so the toolbar's overflow can't clip it.

const styles = {
  container: { position: 'relative', display: 'inline-flex' },
  trigger: {
    display: 'inline-flex', alignItems: 'center', gap: '8px',
    padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px',
    backgroundColor: '#fff', color: '#475569', fontSize: '14px',
    cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit',
  },
  triggerActive: { borderColor: '#c7d2fe', color: '#4338ca', backgroundColor: '#eef2ff' },
  triggerPlaceholder: { color: '#94a3b8' },
  clearX: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '18px', height: '18px', borderRadius: '50%', border: 'none',
    backgroundColor: 'transparent', color: '#6366f1', cursor: 'pointer',
    fontSize: '15px', lineHeight: 1, padding: 0,
  },
  menu: {
    position: 'fixed', zIndex: 1200, backgroundColor: '#fff',
    border: '1px solid #e2e8f0', borderRadius: '12px',
    boxShadow: '0 12px 32px rgba(15,23,42,0.16)', padding: '14px',
    width: '286px',
  },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' },
  monthLabel: { fontSize: '14px', fontWeight: '600', color: '#1e293b' },
  navBtn: {
    width: '28px', height: '28px', border: '1px solid #e2e8f0', borderRadius: '7px',
    backgroundColor: '#fff', color: '#475569', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '14px', lineHeight: 1, padding: 0,
  },
  navGroup: { display: 'flex', gap: '6px' },
  weekRow: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '4px' },
  weekCell: { textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#94a3b8', padding: '4px 0' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' },
  // Cells carry the range highlight, the button inside carries the endpoint
  // pill — two layers so the band runs edge to edge without gaps.
  cell: { padding: '1px 0', display: 'flex', justifyContent: 'center' },
  cellInRange: { backgroundColor: '#eef2ff' },
  cellRangeStart: { borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' },
  cellRangeEnd: { borderTopRightRadius: '8px', borderBottomRightRadius: '8px' },
  day: {
    width: '32px', height: '32px', border: 'none', borderRadius: '8px',
    backgroundColor: 'transparent', color: '#1e293b', fontSize: '13px',
    cursor: 'pointer', fontFamily: 'inherit', padding: 0,
  },
  dayMuted: { color: '#cbd5e1' },
  dayToday: { boxShadow: 'inset 0 0 0 1px #c7d2fe', fontWeight: '600' },
  dayEndpoint: { backgroundColor: '#4f46e5', color: '#fff', fontWeight: '600' },
  dayDisabled: { color: '#e2e8f0', cursor: 'not-allowed' },
  presets: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' },
  preset: {
    padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '999px',
    backgroundColor: '#fff', color: '#475569', fontSize: '12px',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' },
  linkBtn: { border: 'none', backgroundColor: 'transparent', color: '#4f46e5', fontSize: '13px', cursor: 'pointer', padding: '4px', fontFamily: 'inherit' },
  hint: { fontSize: '12px', color: '#94a3b8' },
};

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const pad = (n) => String(n).padStart(2, '0');

// Local-time formatting. toISOString() would shift the day backwards for any
// timezone ahead of UTC, which in IST silently reports "yesterday".
const toKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const fromKey = (key) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ''));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

const prettyKey = (key) => {
  const d = fromKey(key);
  if (!d) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)} ${d.getFullYear()}`;
};

function DateRangePicker({ startDate, endDate, onChange, placeholder = 'All dates' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  // The in-progress first click. Kept apart from the committed value so an
  // abandoned selection never narrows the list behind the popover.
  const [pendingStart, setPendingStart] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [viewMonth, setViewMonth] = useState(() => {
    const anchor = fromKey(startDate) || new Date();
    return new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  });

  const containerRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuPos = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const width = 286;
    // Keep the popover on screen when the trigger sits near the right edge.
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    setMenuPos({ top: rect.bottom + 6, left });
  }, []);

  useEffect(() => {
    const onDown = (e) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setPendingStart(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updateMenuPos();
    const onMove = () => updateMenuPos();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [isOpen, updateMenuPos]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { setIsOpen(false); setPendingStart(null); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Re-anchor the visible month on the committed start whenever the popover
  // reopens, so returning to a saved filter shows the range rather than today.
  const openMenu = () => {
    const anchor = fromKey(startDate) || new Date();
    setViewMonth(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
    setPendingStart(null);
    setHovered(null);
    setIsOpen(true);
  };

  const todayKey = toKey(new Date());

  // Six weeks of cells, padded with the neighbouring months so the grid never
  // reflows between months.
  const cells = useMemo(() => {
    const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const gridStart = addDays(first, -first.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = addDays(gridStart, i);
      return { key: toKey(d), day: d.getDate(), outside: d.getMonth() !== viewMonth.getMonth() };
    });
  }, [viewMonth]);

  // While picking, preview against the hovered day; otherwise show the
  // committed range.
  const [rangeFrom, rangeTo] = useMemo(() => {
    if (pendingStart) {
      const other = hovered || pendingStart;
      return pendingStart <= other ? [pendingStart, other] : [other, pendingStart];
    }
    return [startDate || null, endDate || startDate || null];
  }, [pendingStart, hovered, startDate, endDate]);

  const commit = (from, to) => {
    onChange({ startDate: from, endDate: to });
    setIsOpen(false);
    setPendingStart(null);
    setHovered(null);
  };

  const pickDay = (key) => {
    if (key > todayKey) return; // no RC record can be updated in the future
    if (!pendingStart) {
      setPendingStart(key);
      setHovered(key);
      return;
    }
    commit(pendingStart <= key ? pendingStart : key, pendingStart <= key ? key : pendingStart);
  };

  const applyPreset = (days) => {
    const today = new Date();
    // Inclusive of today, so "Last 7 days" spans today and the six before it.
    commit(toKey(addDays(today, -(days - 1))), toKey(today));
  };

  const applyThisMonth = () => {
    const now = new Date();
    commit(toKey(new Date(now.getFullYear(), now.getMonth(), 1)), toKey(now));
  };

  const label = startDate || endDate
    ? (startDate && endDate && startDate !== endDate
        ? `${prettyKey(startDate)} – ${prettyKey(endDate)}`
        : prettyKey(startDate || endDate))
    : placeholder;

  const hasValue = Boolean(startDate || endDate);

  const menu = (
    <div ref={menuRef} style={{ ...styles.menu, top: menuPos.top, left: menuPos.left }}>
      <div style={styles.head}>
        <span style={styles.monthLabel}>
          {MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
        </span>
        <div style={styles.navGroup}>
          <button
            type="button"
            aria-label="Previous month"
            style={styles.navBtn}
            onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
          >‹</button>
          <button
            type="button"
            aria-label="Next month"
            style={styles.navBtn}
            onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
          >›</button>
        </div>
      </div>

      <div style={styles.weekRow}>
        {WEEKDAYS.map((w) => <div key={w} style={styles.weekCell}>{w}</div>)}
      </div>

      <div style={styles.grid} onMouseLeave={() => pendingStart && setHovered(pendingStart)}>
        {cells.map((c) => {
          const inRange = rangeFrom && rangeTo && c.key >= rangeFrom && c.key <= rangeTo;
          const isEndpoint = c.key === rangeFrom || c.key === rangeTo;
          const future = c.key > todayKey;
          return (
            <div
              key={c.key}
              style={{
                ...styles.cell,
                ...(inRange && !isEndpoint ? styles.cellInRange : null),
                ...(inRange && c.key === rangeFrom ? { ...styles.cellInRange, ...styles.cellRangeStart } : null),
                ...(inRange && c.key === rangeTo ? { ...styles.cellInRange, ...styles.cellRangeEnd } : null),
              }}
            >
              <button
                type="button"
                disabled={future}
                onClick={() => pickDay(c.key)}
                onMouseEnter={() => pendingStart && setHovered(c.key)}
                style={{
                  ...styles.day,
                  ...(c.outside ? styles.dayMuted : null),
                  ...(c.key === todayKey && !isEndpoint ? styles.dayToday : null),
                  ...(isEndpoint && inRange ? styles.dayEndpoint : null),
                  ...(future ? styles.dayDisabled : null),
                }}
              >
                {c.day}
              </button>
            </div>
          );
        })}
      </div>

      <div style={styles.presets}>
        <button type="button" style={styles.preset} onClick={() => applyPreset(1)}>Today</button>
        <button type="button" style={styles.preset} onClick={() => applyPreset(7)}>Last 7 days</button>
        <button type="button" style={styles.preset} onClick={() => applyPreset(30)}>Last 30 days</button>
        <button type="button" style={styles.preset} onClick={applyThisMonth}>This month</button>
      </div>

      <div style={styles.footer}>
        <span style={styles.hint}>
          {pendingStart ? 'Pick the end date' : 'Pick a start date'}
        </span>
        <button type="button" style={styles.linkBtn} onClick={() => commit('', '')}>Clear</button>
      </div>
    </div>
  );

  return (
    <div ref={containerRef} style={styles.container}>
      <button
        type="button"
        onClick={() => (isOpen ? setIsOpen(false) : openMenu())}
        style={{ ...styles.trigger, ...(hasValue ? styles.triggerActive : null) }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
        <span style={hasValue ? null : styles.triggerPlaceholder}>{label}</span>
        {hasValue && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear date range"
            style={styles.clearX}
            onClick={(e) => { e.stopPropagation(); commit('', ''); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); commit('', ''); }
            }}
          >×</span>
        )}
      </button>
      {isOpen && ReactDOM.createPortal(menu, document.body)}
    </div>
  );
}

export default DateRangePicker;
